import { CalendarDaysIcon, CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input, Popover } from '../../components/ui';
import { dayFormatter } from './format';
import { CustomError, DateRange, MAX_DAYS, PRESETS, Preset, validateCustom } from './range';

interface RangePickerProps {
    value: DateRange;
    /** Today in the venue's time zone: dates after it cannot be chosen. */
    today: string;
    onPreset: (preset: Preset) => void;
    onCustom: (from: string, to: string) => void;
}

const ITEM = 'flex min-h-11 w-full items-center gap-2 rounded-control px-3 text-left text-small text-strong hover:bg-hover';

const CustomForm = ({ value, today, onApply }: { value: DateRange; today: string; onApply: (from: string, to: string) => void }) => {
    const { t } = useTranslation('home');
    const [from, setFrom] = useState(value.from);
    const [to, setTo] = useState(value.to);
    const [error, setError] = useState<CustomError | null>(null);

    const submit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const problem = validateCustom(from, to, today);
        setError(problem);
        if (!problem) onApply(from, to);
    };

    return (
        <form noValidate onSubmit={submit} className="mt-2 grid gap-3 border-t border-hairline p-3">
            <p className="text-small font-semibold text-strong">{t('range.custom')}</p>
            <div className="grid grid-cols-2 gap-3">
                <Field label={t('range.from')}>
                    {(c) => <Input {...c} type="date" max={today} value={from} onChange={(e) => setFrom(e.target.value)} />}
                </Field>
                <Field label={t('range.to')}>
                    {(c) => <Input {...c} type="date" max={today} value={to} onChange={(e) => setTo(e.target.value)} />}
                </Field>
            </div>
            <p className="text-caption text-muted">{t('range.limit', { max: MAX_DAYS })}</p>
            {error && (
                <p role="alert" className="text-caption text-danger">
                    {t(`range.error.${error}`, { max: MAX_DAYS })}
                </p>
            )}
            <Button type="submit" variant="strong">
                {t('range.apply')}
            </Button>
        </form>
    );
};

/** Time range menu: six presets and a from-to pair of native date inputs. */
export const RangePicker = ({ value, today, onPreset, onCustom }: RangePickerProps) => {
    const { t, i18n } = useTranslation('home');
    const short = dayFormatter(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' });
    const label = value.kind === 'custom' ? `${short(value.from)} – ${short(value.to)}` : t(`range.${value.kind}`);

    return (
        <Popover
            trigger={
                <>
                    <CalendarDaysIcon className="size-5" aria-hidden="true" />
                    {label}
                </>
            }
            triggerLabel={`${t('range.label')}: ${label}`}
        >
            {(close) => (
                <>
                    <ul>
                        {PRESETS.map((p) => (
                            <li key={p}>
                                <button
                                    type="button"
                                    aria-current={value.kind === p ? 'true' : undefined}
                                    className={clsx(ITEM, value.kind === p && 'font-semibold')}
                                    onClick={() => {
                                        onPreset(p);
                                        close();
                                    }}
                                >
                                    <CheckIcon className={clsx('size-5 shrink-0', value.kind !== p && 'invisible')} aria-hidden="true" />
                                    {t(`range.${p}`)}
                                </button>
                            </li>
                        ))}
                    </ul>
                    <CustomForm
                        value={value}
                        today={today}
                        onApply={(f, to) => {
                            onCustom(f, to);
                            close();
                        }}
                    />
                </>
            )}
        </Popover>
    );
};
